<template>
  <div id="accordionSetupFeaturedImage" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingSetupFeaturedImage" class="accordion-header">
        <button
          aria-controls="collapseSetupFeaturedImage"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseSetupFeaturedImage"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="description" size="xxl" space="p-12" />
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t("clientTour.featuredImage") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseSetupFeaturedImage"
        aria-labelledby="headingSetupFeaturedImage"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionSetupFeaturedImage"
      >
        <div class="accordion-body pb-32 pb-xl-48">
          <div class="d-flex flex-column">
            <v-picture v-if="!!featuredImage" class="d-block image-container mb-16">
              <img :alt="$t('clientTour.featuredImage')" :src="featuredImage.storageUrl" />
            </v-picture>

            <v-uploader
              ref="uploader"
              :multiple="false"
              :max-items="1"
              :after-upload="onChange"
              route-prefix="api/s3"
              @removed="onChange"
            />

            <v-button
              :disabled="disableButton || mediaUuids.length === 0"
              class="mt-16 align-self-end"
              size="sm"
              variant="primary"
              @click="confirmMedia"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { DateTime } from "luxon";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import VUploader from "@/components/vendor/upload/VUploader.vue";
import Media from "@/api/objects/Media";
import { getHummanSize } from "@/helpers/General";
import VPicture from "@/components/vendor/basic/picture/VPicture.vue";
import Template from "@/api/objects/Template";
import { useToast } from "vue-toastification";

const props = defineProps<{
  agency: string;
  template: Template;
}>();
const toast = useToast();

const media = ref<any[]>([]);
const disableButton = ref<boolean>(false);
const featuredImage = ref<Media>({} as Media);
const uploader = ref<any>(null);

const mediaUuids = computed(() => {
  return media.value.map((mediaItem: any) => ({
    uuid: mediaItem.attributes.uuid,
    name: mediaItem.attributes.name,
    collection: "activity_image",
  }));
});

const onChange = () => {
  if (uploader.value) {
    media.value = uploader.value.mediaLibrary.state.media;
  }
};

const confirmMedia = async () => {
  try {
    disableButton.value = true;

    if (featuredImage.value?.id) {
      await api.media.delete("s3", featuredImage.value.id);
    }

    const data = {
      media: mediaUuids.value,
      model_type: "activity",
      collection: "activity_image",
      model_id: props.template.id,
    };

    const response = await api.media.confirm("s3", data);
    toast.success(response.message, {
      position: "bottom-right",
    });

    media.value = [];

    if (uploader.value) {
      uploader.value.mediaLibrary.state.media = [];
    }

    featuredImage.value = props.template.featuredImage;

    disableButton.value = false;
  } catch (e: any) {
    disableButton.value = false;
    console.error(e);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: "bottom-right",
    });
  }
};

onMounted(() => {
  featuredImage.value = props.template.featuredImage;
});
</script>

<style lang="scss" scoped>
.image-container {
  :deep(img) {
    height: 100%;
    max-height: 14rem;
  }
}
</style>
